import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Row,
  Col,
  Card,
  Button,
  Empty,
  Spin,
  Alert,
  Modal,
  message,
  Tag
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  HeartOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useStores } from '../../stores';
import { getAnimalNameWithEmoji } from '../../utils/animalEmojis';
import './MyPetsPage.scss';

const { confirm } = Modal;

const MyPetsPage = observer(() => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { petStore } = useStores();

  useEffect(() => {
    petStore.fetchMyPets();
  }, []);

  const handleAddPet = () => {
    navigate('/pets/add');
  };

  const handleEditPet = (petId) => {
    navigate(`/pets/edit/${petId}`);
  };

  const handleDeletePet = (pet) => {
    confirm({
      title: t('pets.deleteModal.title'),
      icon: <DeleteOutlined />,
      content: t('pets.deleteModal.message', { name: pet.name }),
      okText: t('pets.deleteModal.yesDelete'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      async onOk() {
        const result = await petStore.deletePet(pet.id);
        if (result.success) {
          message.success(t('pets.deleteSuccess', { name: pet.name }));
        } else {
          message.error(result.error || t('pets.deleteFailed'));
        }
      }
    });
  };

  const getSizeLabel = (size) => {
    const sizeLabels = {
      SMALL: t('pets.addEdit.sizes.small'),
      MEDIUM: t('pets.addEdit.sizes.medium'),
      LARGE: t('pets.addEdit.sizes.large'),
      EXTRA_LARGE: t('pets.addEdit.sizes.extraLarge')
    };
    return sizeLabels[size] || size;
  };

  // Get pet photo URL based on available sizes
  const getPetPhotoUrl = (photo) => {
    if (!photo) return null;

    // VersatileImageField returns an object with sizes
    if (typeof photo === 'object') {
      return photo.medium || photo.large || photo.full_size;
    }

    // Fallback for old format (direct URL string)
    return photo;
  };

  const renderPetCard = (pet) => {
    const photoUrl = getPetPhotoUrl(pet.photo);
    const categoryName = i18n.language === 'el'
      ? (pet.animal_category_name_el || pet.animal_category_name)
      : pet.animal_category_name;

    return (
      <Card
        key={pet.id}
        className="pet-card"
        cover={
          photoUrl ? (
            <div className="pet-photo">
              <img src={photoUrl} alt={pet.name} />
            </div>
          ) : (
            <div className="pet-photo-placeholder">
              <HeartOutlined />
            </div>
          )
        }
      actions={[
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => handleEditPet(pet.id)}
        >
          {t('common.edit')}
        </Button>,
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDeletePet(pet)}
        >
          {t('common.delete')}
        </Button>
      ]}
    >
      <div className="pet-card-content">
        <h3 className="pet-name">{pet.name}</h3>

        <div className="pet-info">
          <div className="info-item">
            <span className="label">{t('pets.fields.type')}</span>
            <Tag color="blue">{getAnimalNameWithEmoji(categoryName)}</Tag>
          </div>

          {pet.breed_name && (
            <div className="info-item">
              <span className="label">{t('pets.fields.breed')}</span>
              <span className="value">{pet.breed_name}</span>
            </div>
          )}

          {pet.size && (
            <div className="info-item">
              <span className="label">{t('pets.fields.size')}</span>
              <Tag color="green">{getSizeLabel(pet.size)}</Tag>
            </div>
          )}

          {pet.age !== null && pet.age !== undefined && (
            <div className="info-item">
              <span className="label">{t('pets.fields.age')}</span>
              <span className="value">
                {pet.age} {pet.age === 1 ? t('pets.fields.yearOld') : t('pets.fields.yearsOld')}
              </span>
            </div>
          )}

          {pet.birthday && (
            <div className="info-item">
              <span className="label">{t('pets.fields.birthday')}</span>
              <span className="value">{dayjs(pet.birthday).format('MMMM D, YYYY')}</span>
            </div>
          )}

          {pet.microchip_code && (
            <div className="info-item">
              <span className="label">{t('pets.fields.microchip')}</span>
              <span className="value">{pet.microchip_code}</span>
            </div>
          )}
        </div>
      </div>
    </Card>
    );
  };

  if (petStore.loading && petStore.myPets.length === 0) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="my-pets-page">
      <div className="page-header">
        <div>
          <h1>{t('pets.title')}</h1>
          <p>{t('pets.description')}</p>
        </div>
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={handleAddPet}
        >
          {t('pets.addNew')}
        </Button>
      </div>

      {petStore.error && (
        <Alert
          message={t('common.error')}
          description={petStore.error}
          type="error"
          closable
          onClose={() => petStore.clearError()}
          style={{ marginBottom: 16 }}
        />
      )}

      {petStore.myPets.length === 0 ? (
        <Empty
          description={t('pets.noAnimals')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddPet}>
            {t('pets.addFirst')}
          </Button>
        </Empty>
      ) : (
        <Row gutter={[16, 16]} className="pets-grid">
          {petStore.myPets.map(renderPetCard)}
        </Row>
      )}
    </div>
  );
});

export default MyPetsPage;
